import type { AnyCard, EarningsCard, PreparationCard, EventCard } from "../../types/cards";
import { useLanguage } from "../../i18n/LanguageContext";

// Per-deck visual style — solid dark + coloured accent strip
const DECK_CONFIG: Record<string, {
  bg: string;
  accent: string;       // top strip
  border: string;
  label: string;        // fallback (overridden by t.card)
  emoji: string;
  amountColor: string;
}> = {
  earnings: {
    bg: "bg-slate-800",
    accent: "bg-blue-500",
    border: "border-blue-500/40",
    label: "EARNINGS",
    emoji: "💰",
    amountColor: "text-yellow-300",
  },
  preparation: {
    bg: "bg-slate-800",
    accent: "bg-violet-500",
    border: "border-violet-500/40",
    label: "PREPARATION",
    emoji: "📚",
    amountColor: "text-violet-300",
  },
  event: {
    bg: "bg-slate-800",
    accent: "bg-amber-500",
    border: "border-amber-500/40",
    label: "EVENT",
    emoji: "⚡",
    amountColor: "text-green-400",
  },
};

interface Props { card: AnyCard; compact?: boolean; }

export default function CardFace({ card, compact }: Props) {
  const { t } = useLanguage();
  const cfg = DECK_CONFIG[card.deck];

  const deckLabel = card.deck === "earnings" ? t.card.earningsLabel
    : card.deck === "preparation" ? t.card.prepLabel
    : t.card.eventLabel;

  const iconLabel = (key: string) => t.icons[key as keyof typeof t.icons] ?? key;

  const ec = card as EarningsCard;
  const pc = card as PreparationCard;
  const ev = card as EventCard;

  // Use translated strings — falls back to card data if not translated yet
  const tCard =
    card.deck === "event"       ? (t.events?.[card.id]      ?? null) :
    card.deck === "earnings"    ? (t.earnings?.[card.id]    ?? null) :
    card.deck === "preparation" ? (t.preparation?.[card.id] ?? null) : null;
  const displayTitle       = tCard?.title              ?? card.title;
  const displayDescription = tCard?.description        ?? card.description;

  return (
    <div className={`w-full ${cfg.bg} border ${cfg.border} rounded-2xl flex flex-col select-none shadow-lg overflow-hidden`}>

      {/* Coloured accent strip */}
      <div className={`${cfg.accent} px-4 py-2 flex items-center justify-between`}>
        <span className="text-white text-xs font-black tracking-widest opacity-90">{deckLabel}</span>
        <span className="text-lg leading-none">{cfg.emoji}</span>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col justify-between p-4 gap-2">
        <div>
          <div className="font-display font-black text-white text-base leading-snug">{displayTitle}</div>
          {!compact && (
            <div className="text-white/55 text-xs leading-relaxed mt-1">{displayDescription}</div>
          )}
        </div>

        {/* Footer stats */}
        {!compact && (
          <div className="space-y-1">
            {card.deck === "earnings" && (
              <>
                <div className={`font-black text-2xl ${cfg.amountColor}`}>+{ec.baseIncome} 🪙</div>
                {ec.requiredIcons.length > 0 && (
                  <div className="text-xs text-white/45 flex flex-wrap gap-1">
                    <span className="text-white/30">{t.card.boostedBy}</span>
                    {ec.requiredIcons.map((i) => (
                      <span key={i} className="bg-white/10 rounded px-1">{iconLabel(i)}</span>
                    ))}
                  </div>
                )}
                {ec.wlbCost > 0 && (
                  <div className="text-xs text-orange-400/80">{t.card.wlbCost(ec.wlbCost)}</div>
                )}
              </>
            )}

            {card.deck === "preparation" && (
              <>
                <div className="text-xs text-white/50 flex flex-wrap gap-1">
                  <span className="text-white/30">{t.card.grants}</span>
                  {pc.grantsIcons.map((i) => (
                    <span key={i} className="bg-violet-500/25 text-violet-300 rounded px-1">{iconLabel(i)}</span>
                  ))}
                </div>
                {pc.oneTimeBonus ? (
                  <div className={`font-bold text-lg ${cfg.amountColor}`}>+{pc.oneTimeBonus} 🪙 {t.card.bonus}</div>
                ) : null}
                {pc.wlbCost > 0 && (
                  <div className="text-xs text-orange-400/80">{t.card.wlbCost(pc.wlbCost)}</div>
                )}
              </>
            )}

            {card.deck === "event" && (
              <>
                {ev.cashDelta !== 0 && (
                  <div className={`font-black text-2xl ${ev.cashDelta > 0 ? "text-green-400" : "text-red-400"}`}>
                    {ev.cashDelta > 0 ? "+" : ""}{ev.cashDelta} 🪙
                  </div>
                )}
                {ev.wlbDelta !== 0 && (
                  <div className={`text-xs font-semibold ${ev.wlbDelta > 0 ? "text-green-400/80" : "text-orange-400/80"}`}>
                    {t.card.wlbChange(ev.wlbDelta)}
                  </div>
                )}
                {ev.affectsAll && (
                  <div className="text-xs text-amber-400">{t.card.affectsAll}</div>
                )}
                {ev.conditionIcon && (
                  <div className="text-xs text-white/40">
                    {iconLabel(ev.conditionIcon)} {t.card.halvespenalty}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
